import React, { useEffect, useRef } from "react";

/**
 * FireworksBurst — a real fireworks show over the dance floor.
 * While `active` is true, rockets launch from the bottom edge and explode
 * across the sky into gravity-driven spark showers. Canvas-based, transparent,
 * and fades out naturally after `active` turns false.
 */
const COLORS = ["#FCF6BA", "#D4AF37", "#FF7AA8", "#FFC4D6", "#FFE5A0", "#FF8C5C", "#B97AC6", "#7ADFFF", "#9CFF8C"];

export default function FireworksBurst({ active }) {
  const canvasRef = useRef(null);
  const stRef = useRef({ rockets: [], sparks: [], raf: 0, running: false });
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const st = stRef.current;

    const explode = (r) => {
      const n = (70 + Math.random() * 45) | 0;
      const alt = COLORS[(Math.random() * COLORS.length) | 0];
      const maxLife = 55 + (Math.random() * 30) | 0;
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * Math.PI * 2 + Math.random() * 0.25;
        const sp = r.ring ? 4.6 + Math.random() * 0.7 : 1.4 + Math.random() * 4.6;
        st.sparks.push({
          x: r.x, y: r.y, px: r.x, py: r.y,
          vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
          color: Math.random() < 0.35 ? alt : r.color,
          size: 1.6 + Math.random() * 2.4,
          life: maxLife, maxLife,
          twinkle: Math.random() < 0.4,
        });
      }
    };

    const ensureLoop = () => {
      if (st.running) return;
      st.running = true;
      const step = () => {
        const canvas = canvasRef.current;
        if (!canvas) { st.running = false; return; }
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        const W = canvas.clientWidth, H = canvas.clientHeight;
        if (canvas.width !== (W * dpr) | 0) { canvas.width = (W * dpr) | 0; canvas.height = (H * dpr) | 0; }
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, W, H);
        ctx.lineCap = "round";

        st.rockets = st.rockets.filter((r) => {
          r.x += r.vx; r.y += r.vy; r.vy += 0.085;
          ctx.globalAlpha = 0.9;
          ctx.strokeStyle = "#FFECAA";
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(r.x - r.vx * 4, r.y - r.vy * 4); ctx.lineTo(r.x, r.y); ctx.stroke();
          ctx.globalAlpha = 1;
          if (r.y <= r.targetY || r.vy >= -1.2) { explode(r); return false; }
          return true;
        });

        st.sparks = st.sparks.filter((p) => {
          p.px = p.x; p.py = p.y;
          p.vx *= 0.985;
          p.vy = p.vy * 0.985 + 0.055;
          p.x += p.vx; p.y += p.vy;
          p.life -= 1;
          const a = Math.max(p.life / p.maxLife, 0);
          ctx.globalAlpha = p.twinkle && (p.life & 4) ? a * 0.35 : a;
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.size;
          ctx.beginPath(); ctx.moveTo(p.px, p.py); ctx.lineTo(p.x, p.y + 0.1); ctx.stroke();
          return p.life > 0 && p.y < H + 30;
        });
        ctx.globalAlpha = 1;

        if (st.rockets.length || st.sparks.length || activeRef.current) {
          st.raf = requestAnimationFrame(step);
        } else {
          ctx.clearRect(0, 0, W, H);
          st.running = false;
        }
      };
      st.raf = requestAnimationFrame(step);
    };

    if (!active) return undefined;

    const launch = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = canvas.clientWidth, H = canvas.clientHeight;
      st.rockets.push({
        x: W * (0.1 + Math.random() * 0.8),
        y: H + 10,
        vx: (Math.random() - 0.5) * 1.4,
        vy: -(H * (0.014 + Math.random() * 0.005)),
        targetY: H * (0.14 + Math.random() * 0.4),
        color: COLORS[(Math.random() * COLORS.length) | 0],
        ring: Math.random() < 0.28,
      });
    };

    launch();
    setTimeout(launch, 180);
    const iv = setInterval(() => { if (activeRef.current) launch(); }, 460);
    ensureLoop();
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => () => cancelAnimationFrame(stRef.current.raf), []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-30"
      data-testid="fireworks-burst"
    />
  );
}
